import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import useAddDisapprovalReason from "@babackapp/private-api-client/client/use-add-disapproval-reason";
import useDeleteDisapprovalReason from "@babackapp/private-api-client/client/use-delete-disapproval-reason";
import useReorderDisapprovalReasons from "@babackapp/private-api-client/client/use-reorder-disapproval-reasons";
import useUpdateDisapprovalReason from "@babackapp/private-api-client/client/use-update-disapproval-reason";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  DisapprovalReasonEditor,
  type DisapprovalReasonDraft,
} from "./disapproval-reason-editor";
import { DisapprovalReasonRow } from "./disapproval-reason-row";

const NEW_REASON = "new" as const;

export function DisapprovalReasonList() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutate: addReason, isPending: isAdding } =
    useAddDisapprovalReason(shop);
  const { mutate: updateReason, isPending: isUpdating } =
    useUpdateDisapprovalReason(shop);
  const { mutate: archiveReason, isPending: isArchiving } =
    useDeleteDisapprovalReason(shop);
  const { mutate: reorderReasons, isPending: isReordering } =
    useReorderDisapprovalReasons(shop);

  const [editing, setEditing] = useState<string | typeof NEW_REASON | null>(
    null,
  );
  // Holds the dropped order until the server confirms it. Without it the row
  // springs back to where it was and only moves once the refetch lands.
  const [droppedOrder, setDroppedOrder] = useState<string[] | null>(null);

  const stored = useMemo(
    () => shop.disapprovalReasons.filter((reason) => !reason.archivedAt),
    [shop.disapprovalReasons],
  );

  const reasons = useMemo(() => {
    if (!droppedOrder) return stored;

    const byId = new Map(stored.map((reason) => [reason.id, reason]));
    const dropped = droppedOrder.flatMap((id) => byId.get(id) ?? []);
    // Anything created or restored elsewhere while the call was in flight still
    // shows up, after the reordered ones.
    const rest = stored.filter((reason) => !droppedOrder.includes(reason.id));

    return [...dropped, ...rest];
  }, [stored, droppedOrder]);

  // Outlives the request on purpose: dropping it on success would flash the old
  // order until the refetch lands. Clears once the server agrees, which also
  // covers a reorder made from another tab.
  useEffect(() => {
    if (!droppedOrder) return;
    const storedIds = stored.map((reason) => reason.id);
    if (storedIds.join() === droppedOrder.join()) setDroppedOrder(null);
  }, [stored, droppedOrder]);

  const isBusy = isAdding || isUpdating || isArchiving || isReordering;

  // Persisted on drop rather than staged behind a save button: the order is the
  // only thing a drag can express, so there is nothing left to confirm.
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const from = reasons.findIndex((reason) => reason.id === active.id);
    const to = reasons.findIndex((reason) => reason.id === over.id);
    if (from === -1 || to === -1) return;

    const ids = arrayMove(reasons, from, to).map((reason) => reason.id);
    setDroppedOrder(ids);
    reorderReasons(ids, {
      // Put the row back where the merchant took it from; the mutation's own
      // error toast says why.
      onError: () => setDroppedOrder(null),
    });
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h4 className="text-sm font-medium">
              {t("settings.disapprovalReasons.list.title")}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t("settings.disapprovalReasons.list.help")}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={isBusy || editing !== null}
            onClick={() => setEditing(NEW_REASON)}
          >
            <Plus className="size-4" />
            {t("settings.disapprovalReasons.actions.add")}
          </Button>
        </div>

        {reasons.length === 0 && editing === null ? (
          <div className="rounded-md border border-dashed border-border px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {t("settings.disapprovalReasons.list.empty")}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => setEditing(NEW_REASON)}
            >
              <Plus className="size-4" />
              {t("settings.disapprovalReasons.actions.add")}
            </Button>
          </div>
        ) : (
          <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={reasons.map((reason) => reason.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col">
                {reasons.map((reason) =>
                  editing === reason.id ? (
                    <DisapprovalReasonEditor
                      key={reason.id}
                      reason={reason}
                      isSaving={isUpdating}
                      onCancel={() => setEditing(null)}
                      onSave={(draft: DisapprovalReasonDraft) =>
                        updateReason(
                          {
                            reasonId: reason.id,
                            label: draft.label,
                            help: draft.help || null,
                            message: draft.message,
                          },
                          { onSuccess: () => setEditing(null) },
                        )
                      }
                    />
                  ) : (
                    <DisapprovalReasonRow
                      key={reason.id}
                      reason={reason}
                      isBusy={isBusy}
                      onEdit={() => setEditing(reason.id)}
                      onArchive={() => archiveReason(reason.id)}
                    />
                  ),
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {editing === NEW_REASON && (
          <DisapprovalReasonEditor
            isSaving={isAdding}
            onCancel={() => setEditing(null)}
            onSave={(draft: DisapprovalReasonDraft) =>
              addReason(
                {
                  label: draft.label,
                  help: draft.help || null,
                  message: draft.message,
                },
                { onSuccess: () => setEditing(null) },
              )
            }
          />
        )}

        <p className="text-xs text-muted-foreground">
          {t("settings.disapprovalReasons.list.archivedNote")}
        </p>
      </CardContent>
    </Card>
  );
}
